import { useCallback, useEffect, useState } from 'react';
import { clientFor, useAccounts } from '../auth/store';
import { useT } from '../i18n';
import { MailError, type Forwarding, type Keep, type RulesState, type Vacation } from '../api/types';
import { useMail } from './MailShell';

/*
    Forwarding and the automatic reply.

    Both live in one Sieve script on the mail server, so both are read and written together and the
    server always answers with the whole state. That is why one save refreshes the other half of the
    screen rather than leaving it showing what it was told a minute ago.

    Two things this screen is careful about, and both are about not lying:

      - STORED IS NOT RUNNING. A Sieve script can be saved, listed, and still do nothing, because
        what runs is whichever one is active. Telling somebody their mail is being forwarded when it
        is not is the worst mistake available here, so `running` is shown rather than assumed.

      - SOMEBODY ELSE'S SCRIPT IS NOT TOUCHED. A mailbox may already hold a filter script written by
        hand. It is shown as it is and the forms are not offered: overwriting one would stop mail
        that somebody is relying on, and they would find out weeks later.

    What the mail server cannot do is left out rather than shown greyed: no `fileinto` and the "move
    to a folder" choice is not in the list at all; no date support and the two date fields are not
    drawn.
*/

const card: React.CSSProperties = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-card)',
    padding: 16,
    display: 'grid',
    gap: 12,
};

const input: React.CSSProperties = {
    height: 30,
    padding: '0 10px',
    background: 'var(--surface2)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--r-btn)',
    outline: 'none',
    fontSize: 'var(--fs-sm)',
    minWidth: 0,
    width: '100%',
};

const primary: React.CSSProperties = {
    height: 30,
    padding: '0 14px',
    background: 'var(--accent)',
    color: 'var(--on-accent)',
    border: 'none',
    borderRadius: 'var(--r-btn)',
    fontSize: 'var(--fs-sm)',
    fontWeight: 600,
};

const ghost: React.CSSProperties = {
    height: 30,
    padding: '0 12px',
    background: 'transparent',
    border: '1px solid var(--border2)',
    borderRadius: 'var(--r-btn)',
    color: 'var(--text-dim)',
    fontSize: 'var(--fs-sm)',
};

function Segmented<T extends string>(props: { value: T; options: { id: T; label: string }[]; onChange: (v: T) => void }) {
    return (
        <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--chip-bg)', border: '1px solid var(--border)', borderRadius: 'var(--r-btn)' }}>
            {props.options.map((o) => {
                const on = o.id === props.value;
                return (
                    <button
                        key={o.id}
                        type="button"
                        data-testid={`forwarding-keep-${o.id}`}
                        onClick={() => props.onChange(o.id)}
                        style={{
                            flex: 1,
                            height: 28,
                            padding: '0 12px',
                            border: 'none',
                            borderRadius: 7,
                            fontSize: 'var(--fs-sm)',
                            fontWeight: on ? 600 : 400,
                            background: on ? 'var(--surface)' : 'transparent',
                            // --text-dim, not --text-mute. Measured with axe on the dark theme:
                            // muted text on the chip background comes to 4.38:1 and the bar is 4.5.
                            // The same three-way control in Settings carried the same shortfall
                            // and was fixed with this one.
                            color: on ? 'var(--accent-strong)' : 'var(--text-dim)',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

export default function ForwardingSettings() {
    const { t } = useT();
    const account = useAccounts((s) => s.accounts.find((a) => a.id === s.activeId) ?? s.accounts[0]);
    const { mailboxes } = useMail();

    const [state, setState] = useState<RulesState | null>(null);
    const [missing, setMissing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [savedNote, setSavedNote] = useState(false);

    // The forms, kept apart from what the server said. The badges above them go on reporting the
    // server's state while somebody is typing, because those two are different things.
    const [to, setTo] = useState<string[]>([]);
    const [draft, setDraft] = useState('');
    const [keep, setKeep] = useState<Keep>('inbox');
    const [folder, setFolder] = useState('');
    const [from, setFrom] = useState('');
    const [until, setUntil] = useState('');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');

    const adopt = useCallback((next: RulesState) => {
        setState(next);
        setTo(next.forwarding.to ?? []);
        setKeep(next.forwarding.keep || 'inbox');
        setFolder(next.forwarding.folder ?? '');
        setFrom(next.vacation.from ?? '');
        setUntil(next.vacation.until ?? '');
        setSubject(next.vacation.subject ?? '');
        setBody(next.vacation.body ?? '');
    }, []);

    const address = account?.email;
    useEffect(() => {
        if (!address) return;
        let live = true;
        setLoading(true);
        clientFor(address)
            .rules()
            .then((next) => {
                if (!live) return;
                // Null means this installation has no ManageSieve server. The tab is not drawn in
                // that case, so arriving here means somebody typed ?tab=forwarding — say so plainly
                // rather than showing an empty form that cannot be saved.
                if (next) adopt(next);
                setMissing(!next);
                setError(null);
            })
            .catch((err: unknown) => {
                if (live) setError(messageOf(err));
            })
            .finally(() => {
                if (live) setLoading(false);
            });
        return () => {
            live = false;
        };
        // No translator in here on purpose. `t` is rebuilt on every render, and an effect that
        // depends on it re-runs on every render — which set loading back to true each time and left
        // the screen saying "loading" for ever. What needs a sentence is translated where it is
        // drawn, not where it is decided.
    }, [address, adopt]);

    const flash = useCallback(() => {
        setSavedNote(true);
        window.setTimeout(() => setSavedNote(false), 1600);
    }, []);

    const run = useCallback(
        async (work: () => Promise<RulesState>) => {
            setError(null);
            try {
                adopt(await work());
                flash();
            } catch (err: unknown) {
                setError(messageOf(err));
            }
        },
        [adopt, flash],
    );

    if (!account) return <div style={card}>{t('identity.noAccount')}</div>;
    if (loading) return <div style={card}>{t('app.loading')}…</div>;
    if (!state) {
        // Carries the error testid so that a screen which cannot load is a visible red in the
        // browser suite rather than a timeout nobody can read.
        return (
            <div style={card} data-testid="forwarding-error">
                {error ?? (missing ? t('contacts.none') : t('app.loading'))}
            </div>
        );
    }

    const client = clientFor(account.email);

    // Zero means the mail server announced no limit and none was configured, so none is imposed.
    // Above zero the screen stops here rather than at save: the server would accept the script,
    // store it, run it and read it back unchanged, and then forward nothing when mail arrived.
    const full = state.maxTargets > 0 && to.length >= state.maxTargets;

    const addAddress = () => {
        const next = draft.trim();
        if (!next || full) return;
        setDraft('');
        if (to.some((existing) => existing.toLowerCase() === next.toLowerCase())) return;
        setTo([...to, next]);
    };

    const keepOptions: { id: Keep; label: string }[] = [
        { id: 'inbox', label: t('forwarding.keep') },
        // Absent rather than disabled: a server without fileinto cannot file a copy anywhere, and
        // an option that fails on save is worse than one that was never offered.
        ...(state.canFileInto ? [{ id: 'folder' as Keep, label: t('forwarding.archive') }] : []),
        { id: 'discard', label: t('forwarding.delete') },
    ];

    const saveForwarding = () =>
        run(() =>
            client.saveForwarding({
                enabled: true,
                to,
                keep,
                folder: keep === 'folder' ? folder : '',
            } satisfies Forwarding),
        );

    const stopForwarding = () =>
        run(() => client.saveForwarding({ enabled: false, to: [], keep: 'inbox', folder: '' }));

    const saveVacation = () =>
        run(() =>
            client.saveVacation({
                enabled: true,
                from: state.canScheduleReply ? from : '',
                until: state.canScheduleReply ? until : '',
                subject,
                body,
            } satisfies Vacation),
        );

    const stopVacation = () =>
        run(() => client.saveVacation({ enabled: false, from: '', until: '', subject: '', body: '' }));

    return (
        <div style={{ display: 'grid', gap: 12 }} data-testid="forwarding-panel">
            {error ? (
                <div style={{ ...card, color: 'var(--red)' }} data-testid="forwarding-error">
                    {error}
                </div>
            ) : null}

            {state.foreign ? (
                <div style={card} data-testid="forwarding-foreign">
                    <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{t('forwarding.title')}</div>
                    <div className="kicker" style={{ lineHeight: 1.5 }}>{t('forwarding.foreign')}</div>
                    <div className="kicker">// {t('forwarding.foreignScript')}</div>
                    <pre
                        style={{
                            margin: 0,
                            padding: 10,
                            background: 'var(--surface2)',
                            border: '1px solid var(--border)',
                            borderRadius: 'var(--r-btn)',
                            fontFamily: 'var(--font-mono)',
                            fontSize: 'var(--fs-sm)',
                            whiteSpace: 'pre-wrap',
                            overflowX: 'auto',
                        }}
                    >
                        {state.script}
                    </pre>
                </div>
            ) : (
                <>
                    <div style={card}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{t('forwarding.title')}</div>
                            <div className="kicker" data-testid="forwarding-status">
                                {/* The server's state, not the form's: somebody typing an address has
                                    not turned anything on yet. */}
                                // {state.forwarding.enabled ? t('forwarding.on') : t('forwarding.off')}
                            </div>
                        </div>

                        {state.forwarding.enabled && !state.running ? (
                            <div className="kicker" style={{ lineHeight: 1.5 }} data-testid="forwarding-not-running">
                                {t('forwarding.notRunning')}
                            </div>
                        ) : null}

                        {to.length ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }} data-testid="forwarding-targets">
                                {to.map((one) => (
                                    <span
                                        key={one}
                                        style={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            height: 27,
                                            padding: '0 6px 0 10px',
                                            background: 'var(--chip-bg)',
                                            border: '1px solid var(--border)',
                                            borderRadius: 'var(--r-btn)',
                                            fontSize: 'var(--fs-sm)',
                                        }}
                                    >
                                        {one}
                                        <button
                                            type="button"
                                            aria-label={`${t('forwarding.remove')} ${one}`}
                                            onClick={() => setTo(to.filter((x) => x !== one))}
                                            style={{
                                                width: 22,
                                                height: 22,
                                                border: 'none',
                                                borderRadius: 'var(--r-btn)',
                                                background: 'transparent',
                                                color: 'var(--text-dim)',
                                                fontSize: 'var(--fs-sm)',
                                            }}
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        ) : null}

                        <div style={{ display: 'flex', gap: 8 }}>
                            <input
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        addAddress();
                                    }
                                }}
                                placeholder="ornek@alanadi.com"
                                style={{ ...input, opacity: full ? 0.5 : 1 }}
                                disabled={full}
                                data-testid="forwarding-address"
                            />
                            <button
                                type="button"
                                onClick={addAddress}
                                disabled={full}
                                style={{ ...ghost, opacity: full ? 0.5 : 1 }}
                                data-testid="forwarding-add"
                            >
                                {t('forwarding.add')}
                            </button>
                        </div>

                        {full ? (
                            <div className="kicker" style={{ lineHeight: 1.5 }} data-testid="forwarding-limit">
                                {t('forwarding.limitReached', { n: state.maxTargets })}
                            </div>
                        ) : null}

                        <div className="kicker">// {t('forwarding.originalTitle')}</div>
                        <Segmented value={keep} options={keepOptions} onChange={setKeep} />

                        {keep === 'folder' ? (
                            <label style={{ display: 'grid', gap: 5 }}>
                                <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-dim)' }}>{t('forwarding.into')}</span>
                                <select
                                    value={folder}
                                    onChange={(e) => setFolder(e.target.value)}
                                    style={input}
                                    data-testid="forwarding-folder"
                                >
                                    <option value="" />
                                    {mailboxes
                                        .filter((box) => box.selectable !== false)
                                        .map((box) => (
                                            // The value is the full IMAP path, which is what the
                                            // server's fileinto wants — not the display name.
                                            <option key={box.id} value={box.id}>
                                                {box.name}
                                            </option>
                                        ))}
                                </select>
                            </label>
                        ) : null}

                        <div className="kicker" style={{ lineHeight: 1.5 }}>{t('forwarding.hint')}</div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                            <button type="button" onClick={saveForwarding} style={primary} data-testid="forwarding-save">
                                {t('app.save')}
                            </button>
                            {state.forwarding.enabled ? (
                                <button type="button" onClick={stopForwarding} style={ghost} data-testid="forwarding-off">
                                    {t('forwarding.turnOff')}
                                </button>
                            ) : null}
                            {savedNote ? <span className="kicker">// {t('forwarding.saved')}</span> : null}
                        </div>
                    </div>

                    <div style={card}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                            <div style={{ fontSize: 'var(--fs-md)', fontWeight: 600 }}>{t('vacation.title')}</div>
                            {state.canAutoReply ? (
                                <div className="kicker" data-testid="vacation-status">
                                    // {state.vacation.enabled ? t('vacation.onNow') : t('vacation.offNow')}
                                </div>
                            ) : null}
                        </div>

                        {!state.canAutoReply ? (
                            <div className="kicker" style={{ lineHeight: 1.5 }} data-testid="vacation-unsupported">
                                {t('vacation.unsupported')}
                            </div>
                        ) : (
                            <>
                                {state.canScheduleReply ? (
                                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                        <label style={{ display: 'grid', gap: 5, flex: '1 1 160px' }}>
                                            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-dim)' }}>{t('vacation.from')}</span>
                                            <input
                                                type="date"
                                                value={from}
                                                onChange={(e) => setFrom(e.target.value)}
                                                style={input}
                                                data-testid="vacation-from"
                                            />
                                        </label>
                                        <label style={{ display: 'grid', gap: 5, flex: '1 1 160px' }}>
                                            <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-dim)' }}>{t('vacation.to')}</span>
                                            <input
                                                type="date"
                                                value={until}
                                                onChange={(e) => setUntil(e.target.value)}
                                                style={input}
                                                data-testid="vacation-until"
                                            />
                                        </label>
                                    </div>
                                ) : null}

                                <label style={{ display: 'grid', gap: 5 }}>
                                    <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-dim)' }}>{t('vacation.subject')}</span>
                                    <input
                                        value={subject}
                                        onChange={(e) => setSubject(e.target.value)}
                                        placeholder={t('vacation.subjectHint')}
                                        style={input}
                                        data-testid="vacation-subject"
                                    />
                                </label>

                                <label style={{ display: 'grid', gap: 5 }}>
                                    <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-dim)' }}>{t('vacation.body')}</span>
                                    <textarea
                                        value={body}
                                        onChange={(e) => setBody(e.target.value)}
                                        placeholder={t('rich.placeholder.vacation')}
                                        rows={5}
                                        style={{ ...input, height: 'auto', padding: 9, resize: 'vertical' }}
                                        data-testid="vacation-body"
                                    />
                                </label>

                                <div className="kicker" style={{ lineHeight: 1.5 }}>{t('vacation.hint')}</div>

                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                    <button type="button" onClick={saveVacation} style={primary} data-testid="vacation-save">
                                        {state.vacation.enabled ? t('vacation.saveOn') : t('vacation.turnOn')}
                                    </button>
                                    {state.vacation.enabled ? (
                                        <button type="button" onClick={stopVacation} style={ghost} data-testid="vacation-off">
                                            {t('vacation.turnOff')}
                                        </button>
                                    ) : null}
                                </div>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

/*
messageOf keeps the mail server's own words when it has any.

A refused script comes back with the line and the reason the server named, and nothing written here
could replace that — the same reason a bounce carries the server's text rather than a summary of it.
*/
function messageOf(err: unknown): string {
    if (err instanceof MailError) {
        return err.reason ? `${err.message}: ${err.reason}` : err.message;
    }
    return err instanceof Error ? err.message : String(err);
}
